import { isCountAggregateOp } from '@ofmos/shared/field-types';
import type {
  AggregateRequest,
  AggregateValue,
  GroupKeyPart,
  RecordGroupTotals,
} from '@ofmos/shared/record-group';
import type { FieldMetadata, RecordGroupEntry, RecordGroupSet } from '../../data/client';
import { selectOptionsOf } from '../../fields/fieldTypeMeta';

/**
 * EMPTY GROUPS ARE SYNTHESISED ON THE CLIENT, FROM A CLOSED DOMAIN ONLY
 * (`docs/decisions/group-by-aggregation.md` D29).
 *
 * A `GROUP BY` emits a row only for a group that HAS rows - on any backend, in any
 * product. So "Won: 0" on a pipeline, which is the number that motivates the whole
 * feature, cannot come from the server: nothing over there knows the group was supposed to
 * exist. What DOES know is the metadata the browser already holds, and only for the two
 * dimensions whose domain is closed.
 *
 * | Dimension | Closed domain | Empty groups |
 * |---|---|---|
 * | SELECT | the metadata option list | synthesised |
 * | BOOLEAN | true, false, no value | synthesised |
 * | RELATION MANY_TO_ONE | would be every record of the target object | not synthesised |
 * | DATE / DATETIME bucket | would be every period in an unbounded range | not synthesised |
 *
 * THIS IS THE ONE PLACE IN THE MODULE WHERE A NUMBER ON SCREEN WAS NOT PRODUCED BY THE
 * STATEMENT THAT PRODUCED ITS NEIGHBOURS, and that concession is why the arithmetic here
 * is copied from the server rather than invented: over zero rows every count-family
 * operation is 0 and `sum`, `avg`, `min` and `max` are NULL, which is exactly the row the
 * grand-total statement returns for a filter matching nothing (obligation 40). Coalescing
 * a `sum` to 0 here would be Twenty's client-side zero substitution, which D12 condemns in
 * writing.
 *
 * ONE FUNCTION DOES THE SYNTHESIS and the board calls the same one (D23), because a board
 * column at 0 and a table section at 0 are the same claim and must be computed once.
 */

/** The values a dimension can take, in the order they are shown. */
export type ClosedDomain = readonly GroupKeyPart[];

/**
 * The domain a dimension can be completed from, or `null` when it has none.
 *
 * `null` is what disables the "hide empty groups" toggle rather than hiding it (D29): a
 * control that appears and disappears as the user changes dimension is how a control gets
 * mistrusted, and `views/validate.ts` refuses `hide_empty_groups: true` beside one of
 * these, so a view written by hand cannot claim a state the UI cannot produce.
 */
export function closedDomainOf(field: FieldMetadata): ClosedDomain | null {
  if (field.type === 'SELECT') {
    return [
      ...selectOptionsOf(field).map<GroupKeyPart>((option) => ({
        kind: 'option',
        optionId: option.id,
      })),
      // The no-value group is last in every dimension, unconditionally (D16).
      { kind: 'null' },
    ];
  }
  if (field.type === 'BOOLEAN') {
    return [{ kind: 'boolean', value: false }, { kind: 'boolean', value: true }, { kind: 'null' }];
  }
  return null;
}

/**
 * THE GROUPS, WITH THE MISSING MEMBERS OF A CLOSED DOMAIN PUT BACK, in domain order.
 *
 * ONE REFUSAL, and it is the reason this takes the whole result rather than just the
 * groups: when `hasMoreGroups` is true the list was CUT by the group cap, and a value the
 * server never got to is not a value with no rows. Synthesising over a truncated list
 * would turn "we did not fetch this group" into "this group is empty", which is the exact
 * class of lie the module exists to prevent - so the groups come back untouched instead.
 */
export function withEmptyGroups(
  result: RecordGroupSet,
  domain: ClosedDomain,
  requested: readonly AggregateRequest[] = [],
): readonly RecordGroupEntry[] {
  if (result.hasMoreGroups || domain.length === 0) return result.groups;

  const found = new Map(result.groups.map((group) => [partKey(group.key[0]), group]));
  const synthesised = domain.map<RecordGroupEntry>(
    (part) =>
      found.get(partKey(part)) ?? {
        key: [part],
        labels: [null],
        records: [],
        ...emptyGroupTotals(requested, result.total),
      },
  );
  // A group the server returned whose value is NOT in the domain - an option removed from
  // the field while rows still carry it - keeps its place at the end rather than vanishing.
  const extra = result.groups.filter(
    (group) => !domain.some((part) => partKey(part) === partKey(group.key[0])),
  );
  return [...synthesised, ...extra];
}

/**
 * D12's operation table evaluated over ZERO ROWS, index-aligned with the request.
 *
 * The ARM of each answer is read off the grand-total row rather than derived from field
 * metadata: the server decided there whether an aggregate splits by currency, and which
 * codes it discovered, so reading its decision is the only way this cannot disagree with
 * it. What is decided here is only the count/value split, which is `isCountAggregateOp` -
 * the same function the server's own renderer reads.
 */
export function emptyGroupTotals(
  requested: readonly AggregateRequest[],
  total: RecordGroupTotals,
): RecordGroupTotals {
  return {
    count: 0,
    aggregates: requested.map((request, index) =>
      zeroRowValue(isCountAggregateOp(request.op), total.aggregates[index]),
    ),
  };
}

function zeroRowValue(isCount: boolean, shape: AggregateValue | undefined): AggregateValue {
  if (shape?.kind === 'currency') {
    return {
      kind: 'currency',
      amounts: shape.amounts.map((amount) => ({
        currencyCode: amount.currencyCode,
        amountMicros: null,
      })),
    };
  }
  return { kind: 'value', value: isCount ? 0 : null };
}

/**
 * One key part as a string, for lookup only. NOT `encodeGroupKey`: that is the ONE
 * encoding of a whole key (D5), and using it on a single part would suggest the two are
 * interchangeable when a key is a tuple and this is a member of one.
 */
function partKey(part: GroupKeyPart | undefined): string {
  return part === undefined ? '' : JSON.stringify(part);
}
